import { CELL, GROUND } from '../core/config';
import { key, type Building, type City, type Place } from './city';
import { furnish } from './furniture';

/** Service staff have real stock/equipment fixtures in the side aisle. These
 * remain ordinary parented cells; garages and the medical aisle stay clear. */
export function prepareDutyRooms(city: City): City {
  let serial =
    Math.max(
      ...[
        ...city.buildings,
        ...(city.props ?? []),
        ...(city.infrastructure ?? []),
        ...(city.bridges ?? []),
      ].map((b) => b.id),
    ) + 1;
  city.props ??= [];
  city.places ??= [];
  for (const facility of city.facilities ?? []) {
    if (facility.duties) continue;
    const b = city.buildings.find((b) => b.id === facility.buildingId)!;
    const duties: Place[] = [];
    for (let i = 0; i < facility.bays.length; i++) {
      const z = b.z + (i ? 8.5 : 5.5) * CELL,
        x = b.x + 2.0,
        supportCell = b.id * 1e6 + key(1, 0, Math.floor((z - b.z) / CELL)),
        id = serial++,
        action =
          facility.kind === 'fire'
            ? 'Hose and breathing-set inspection'
            : facility.kind === 'hospital'
              ? 'Medical supply check'
              : facility.kind === 'police'
                ? 'Radio and dispatch check'
                : 'Recovery equipment check';
      const item: Building = {
        id,
        name: action,
        kind: 'furniture',
        furniture: 'cabinet',
        category: 'prop',
        x: b.x + 1.27,
        z: z - 0.45,
        baseY: GROUND + 0.2,
        w: 1,
        d: 1,
        h: 2,
        color: 0x758c86,
        accent: 0xc9be9c,
        parentCell: supportCell,
        cells: [
          {
            id: id * 1e6,
            x: 0,
            y: 0,
            z: 0,
            hp: 2.8,
            material: 'metal',
            role: 'prop',
            color: 0x758c86,
            bounds: { x: 0, y: 0, z: 0, w: 0.34 / CELL, h: 1.65 / CELL, d: 0.9 / CELL },
            parts: [
              { x: 0, y: 0, z: 0, w: 0.29 / CELL, h: 1.65 / CELL, d: 0.9 / CELL, color: 0x7a8c83 },
              {
                x: 0.29 / CELL,
                y: 0.8 / CELL,
                z: 0.1 / CELL,
                w: 0.045 / CELL,
                h: 0.5 / CELL,
                d: 0.7 / CELL,
                color: facility.kind === 'police' ? 0x385760 : 0xc1b798,
              },
              {
                x: 0.33 / CELL,
                y: 0.84 / CELL,
                z: 0.37 / CELL,
                w: 0.02 / CELL,
                h: 0.12 / CELL,
                d: 0.05 / CELL,
                color: 0xe1ddc9,
              },
            ],
          },
        ],
      };
      city.props.push(item);
      const place: Place = {
        id: `duty:${facility.id}:${i}`,
        kind: 'staff',
        name: action,
        buildingId: b.id,
        floor: 0,
        x,
        y: GROUND + 0.2,
        z,
        supportCell,
        furnitureId: id,
        facing: Math.PI / 2,
        capacity: 1,
        duration: 14 + i * 3,
      };
      duties.push(place);
      city.places.push(place);
    }
    facility.duties = duties;
    for (const [index, bed] of facility.beds.entries()) {
      const item = city.props.find(
        (p) => p.name === `Hospital bed ${index + 1}` && p.parentCell === bed.supportCell,
      );
      if (!item) continue;
      furnish(item.cells[0], 'bed', index, true);
      item.d = 2;
      bed.furnitureId = item.id;
      bed.pose = {
        x: item.x + 0.625,
        y: bed.y,
        z: item.z + 1.84,
        yaw: 0,
        posture: 'lying',
        seatHeight: 0.49,
        duration: 3,
      };
    }
  }
  return city;
}
